import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  GraduationCap,
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  ChevronRight,
  Plus,
  X,
} from 'lucide-react';
import type { OnboardingTaskKind, TrainingTaskSummary } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useTrainingCatalog,
  useEnrolTraining,
  useUnenrolTraining,
} from '../../hooks/useTrainingCatalog';
import { Hero } from '../../components/ui/Hero';
import { Card, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pill, type PillTone } from '../../components/ui/Pill';
import { SkeletonList } from '../../components/ui/Skeleton';

interface MeTask {
  task_id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  onboarding_kind?: OnboardingTaskKind;
  training_module_id?: string;
  /** Self-enrolled extra rather than required. */
  optional?: boolean;
  training?: TrainingTaskSummary | null;
}
interface MeView {
  person: { first_name: string };
  tasks: MeTask[];
}

/**
 * Landing page of the training portal. Lists the signed-in person's training
 * modules and links into the player.
 *
 * Training is assigned per person (admin assignment, self-enrolment, or the
 * daily "everyone" sweep), so the list is keyed on the person the account is
 * linked to. Learners are always linked: SSO provisions a tagged person row
 * for them on first login. The unlinked empty state is therefore only
 * reached by a staff-only account (admin, coordinator) whose users row was
 * created by hand without a matching people row.
 */
export function TrainingHome() {
  const { data: user, isLoading: sessionLoading } = useCurrentUser();
  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });

  if (sessionLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const firstName = me.data?.person.first_name ?? user.display_name.split(' ')[0];
  const trainingTasks = (me.data?.tasks ?? []).filter((t) => t.onboarding_kind === 'training');

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl space-y-5 px-4 py-8 sm:px-6 sm:py-10">
        <Hero
          eyebrow={
            <>
              <GraduationCap className="h-3 w-3" />
              <span>Training</span>
            </>
          }
          title={`Hi ${firstName}`}
          description="Your training modules and certificates live here."
        />

        {!user.person_id ? (
          <Card>
            <EmptyState
              icon={<GraduationCap className="h-6 w-6" />}
              title="This account isn't linked to a training record"
              description="Ask an administrator to link your account to your staff record and your modules will appear here."
            />
          </Card>
        ) : me.isLoading ? (
          <SkeletonList rows={3} />
        ) : me.error ? (
          <Card>
            <EmptyState
              icon={<AlertCircle className="h-6 w-6" />}
              title="Couldn't load your training"
              description={`The server returned ${
                me.error instanceof ApiError ? me.error.status : 0
              }. Try again in a moment.`}
            />
          </Card>
        ) : trainingTasks.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6" />}
              title="No training outstanding"
              description="You have no training modules assigned right now."
            />
          </Card>
        ) : (
          <Card>
            <ul className="divide-y divide-divider/10">
              {trainingTasks.map((t) => (
                <li key={t.task_id} className="flex items-center gap-2 px-5 py-4">
                  <Link
                    to={`/me/training/${t.task_id}`}
                    className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-token-sm px-2 py-1 transition-colors hover:bg-surface-2"
                  >
                    <GraduationCap className="h-5 w-5 shrink-0 text-accent" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-fg">{t.title}</span>
                        {t.optional ? (
                          <Pill tone="neutral" size="sm">
                            Optional
                          </Pill>
                        ) : null}
                      </div>
                      {t.description ? (
                        <p className="truncate text-xs text-fg-muted">{t.description}</p>
                      ) : null}
                    </div>
                    <TrainingStatusPill task={t} />
                    <ChevronRight className="h-4 w-4 shrink-0 text-fg-dim" />
                  </Link>
                  {t.optional && t.status !== 'complete' && t.training_module_id ? (
                    <RemoveOptionalButton moduleId={t.training_module_id} title={t.title} />
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {user.person_id ? <CatalogSection /> : null}
      </div>
    </main>
  );
}

/**
 * "More training available": every published module the learner hasn't been
 * assigned, with a one-tap self-enrol. Optional extras — their mandatory
 * training is the list above. Hidden entirely when there is nothing to add.
 */
function CatalogSection() {
  const catalog = useTrainingCatalog();
  const enrol = useEnrolTraining();
  const available = (catalog.data ?? []).filter((c) => !c.already_assigned);
  if (catalog.isLoading || available.length === 0) return null;

  return (
    <Card>
      <CardSection className="border-b border-line/10">
        <h2 className="text-sm font-semibold text-fg">More training available</h2>
        <p className="text-xs text-fg-muted">Optional modules you can add to your own training.</p>
      </CardSection>
      <ul className="divide-y divide-divider/10">
        {available.map((c) => (
          <li key={c.module_id} className="flex items-center gap-3 px-5 py-4">
            <GraduationCap className="h-5 w-5 shrink-0 text-fg-muted" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-fg">{c.title}</div>
              {c.description ? (
                <p className="truncate text-xs text-fg-muted">{c.description}</p>
              ) : null}
            </div>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Plus className="h-4 w-4" />}
              loading={enrol.isPending && enrol.variables === c.module_id}
              onClick={() =>
                enrol.mutate(c.module_id, {
                  onSuccess: () => toast.success(`Added “${c.title}” to your training`),
                  onError: (err) =>
                    toast.error(err instanceof ApiError ? err.message : 'Could not add'),
                })
              }
            >
              Add
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Drop a self-enrolled optional module from your own list. */
function RemoveOptionalButton({ moduleId, title }: { moduleId: string; title: string }) {
  const unenrol = useUnenrolTraining();
  return (
    <Button
      variant="ghost"
      size="sm"
      iconLeft={<X className="h-4 w-4" />}
      aria-label={`Remove ${title}`}
      loading={unenrol.isPending}
      onClick={() =>
        unenrol.mutate(moduleId, {
          onSuccess: () => toast.success(`Removed “${title}”`),
          onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not remove'),
        })
      }
    >
      Remove
    </Button>
  );
}

function TrainingStatusPill({ task }: { task: MeTask }) {
  const passed = task.training?.status === 'passed' || task.status === 'complete';
  const inProgress = task.training?.status === 'in_progress';
  const tone: PillTone = passed ? 'success' : inProgress ? 'warning' : 'neutral';
  const label = passed
    ? `Passed${task.training?.best_score != null ? ` · ${task.training.best_score}%` : ''}`
    : inProgress
      ? 'In progress'
      : 'Not started';
  const icon = passed ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />;
  return (
    <Pill tone={tone} icon={icon}>
      {label}
    </Pill>
  );
}
